import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const PRIORITY_TONE = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };

export default function PortalInformationRequests() {
  const [data, setData] = useState(undefined);
  const [active, setActive] = useState(null);
  const load = useCallback(() => portalApi.get("/portal/information-requests").then((r) => setData(r.data)).catch(() => setData({ open: [], completed: [] })), []);
  useEffect(() => { load(); }, [load]);

  if (data === undefined) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-requests">
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Information Requests</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your tax team will ask here for anything they need to complete your work. Responding quickly keeps your filing on track.</p>
      </div>

      {data.open.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center text-emerald-800" data-testid="portal-requests-empty">
          <Icons.CheckCircle2 className="h-8 w-8 mx-auto mb-2" /><p className="text-sm font-medium">You're all caught up — no open requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.open.map((ir) => (
            <button key={ir.id} onClick={() => setActive(ir)} data-testid={`portal-request-${ir.id}`}
              className="w-full text-left bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-[#F5A623] p-4 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${PRIORITY_TONE[ir.priority] || PRIORITY_TONE.low}`}>{ir.priority}</span>
                    <h2 className="font-semibold text-[#171B2E] text-sm truncate">{ir.title}</h2></div>
                  {ir.why_needed && <p className="text-xs text-slate-500 mt-1.5">{ir.why_needed}</p>}
                </div>
                <Icons.ChevronRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      )}

      {data.completed.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Completed</h3>
          <div className="space-y-2">
            {data.completed.map((ir) => (
              <div key={ir.id} className="flex items-center gap-2 text-sm text-slate-500 bg-white rounded-xl border border-slate-100 px-3 py-2">
                <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="line-through">{ir.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && <RespondDialog ir={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}

function RespondDialog({ ir, onClose, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (markComplete) => {
    setBusy(true);
    try {
      await portalApi.post(`/portal/information-requests/${ir.id}/respond`, { comment: comment || null, mark_complete: markComplete });
      toast.success(markComplete ? "Response received — your team will review shortly" : "Response sent to your team"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="portal-request-dialog">
        <DialogHeader><DialogTitle>{ir.title}</DialogTitle>
          <DialogDescription>{ir.description || ir.why_needed || "Your tax team needs this to continue your work."}</DialogDescription></DialogHeader>
        <p className="text-xs text-slate-500">You can upload the document on your <a href="/portal/documents" className="text-[#F5A623] underline">Documents page</a>, then add a note and submit your response.</p>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="portal-request-comment"
          placeholder="Add a comment or answer…" className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
        <DialogFooter>
          <button onClick={() => submit(false)} disabled={busy} className="text-sm px-3 py-2 rounded-lg border border-slate-200" data-testid="portal-request-send">Send comment</button>
          <button onClick={() => submit(true)} disabled={busy} data-testid="portal-request-complete" className="text-sm px-4 py-2 rounded-lg bg-[#F5A623] text-[#171B2E] font-semibold">I'm done responding</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
